import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const theme = `:root{--st-border-color:#636e72;--st-btn-secondary-color:#7977e1;--st-btn-secondary-color-hover:#ffffff;--st-btn-secondary-bg:#000000;--st-btn-secondary-bg-hover:#9a99e8;--st-btn-secondary-bg-active:#7977e1;--st-btn-gray-color:#f5f7f8;--st-btn-gray-color-hover:#ffffff;--st-btn-gray-bg:transparent;--st-btn-gray-bg-hover:#b2bec3;--st-btn-gray-bg-active:#636e72;--st-btn-asphalt-color:#f5f7f8;--st-btn-asphalt-bg:transparent;--st-btn-asphalt-bg-hover:#2d3436;--st-btn-asphalt-bg-active:#1e2224;--st-page-bg:#000000;--st-content-bg:#1e2224;--st-content-bg-hover:#343382;--st-content-bg-active:#232257;--st-text-color-ink:#f5f7f8;--st-text-color-blurple:#9a99e8;--st-text-color-asphalt:#f5f7f8;--st-text-color-lava:#ef8e7d;--st-text-color-emerald:#97e28f;--st-text-color-white:#000000;--st-text-color:#f5f7f8;--st-text-color-support:#f5f7f8;--st-text-color-accent:#9a99e8;--st-nav-color:#ffffff;--st-nav-color-hover:#bcbbf0;--st-nav-bg:#ffffff;--st-nav-bg-hover:#343382;--st-nav-bg-active:#232257;--st-alert-warning-color:#fef3d9;--st-alert-warning-color-inverse:#957628;--st-alert-blurple-color:#ddddf7;--st-alert-blurple-color-inverse:#343382;--st-alert-gray-color:#dfe6e9;--st-alert-gray-color-inverse:#2d3436;--st-alert-error-color:#fad9d4;--st-alert-error-color-inverse:#892817;--st-alert-success-color:#dcf5da;--st-alert-success-color-inverse:#317c29;--st-token-blurple-color:#ddddf7;--st-token-blurple-bg:#5755d9;--st-token-gray-color:#dfe6e9;--st-token-gray-bg:#636e72;--st-token-rose-color:#33111d;--st-token-rose-bg:#ff77a8;--st-token-lava-color:#2e0d08;--st-token-lava-bg:#e96851;--st-token-tangerine-color:#311d0e;--st-token-tangerine-bg:#f8a66c;--st-token-sunshine-color:#32270d;--st-token-sunshine-bg:#f9d069;--st-token-emerald-color:#10290e;--st-token-emerald-bg:#97e28f;--st-token-ice-color:#0d2a33;--st-token-ice-bg:#40d0ff;--st-input-bg:#000000;--st-input-bg-disabled:#2d3436;--st-input-bg-error:#2e0d08;--st-input-handle-color:#ddddf7;--st-input-track-color:#000000;--st-input-track-color-error:#2e0d08;--st-input-icon-color:#636e72;}`;
var modeDark = theme;

// default localstorage ID
const STORAGE_ID_DEFAULT = 'st-mode';
const modeStyles = {
  light: '',
  dark: modeDark
};
const modeRegex = /^st-mode--[a-zA-Z]*$/;

/**
 * Detects the system color mode by reading prefers-color-scheme
 * and returns a string with either dark or light
 */
function getModeFromSystem() {
  if (window && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

/**
 * Reads the last saved mode from localStorage
 */
function getModeFromLocalStore(storageId = STORAGE_ID_DEFAULT) {
  try {
    return localStorage.getItem(storageId);
  } catch {
    return null;
  }
}

/**
 *
 * @param {string} mode Name of mode
 * @param {string} storageId ID for localStorage
 *
 * Saves to localStorage
 */
function saveToStore(mode, storageId = STORAGE_ID_DEFAULT) {
  try {
    return localStorage.setItem(storageId, mode);
  } catch {
    return null;
  }
}

/**
 * Given a mode name, adds a mode-specific CSS class to <body>
 * And inserts a block of CSS into #st-mode-styleblock to set
 * values for the design tokens
 * @param {string} modeParam The mode name to apply styles for
 */
const updateWindowStyles = modeParam => {
  const modeName = modeParam;
  const modeDisplayedName = modeParam === 'auto' ? getModeFromSystem() : modeParam;

  // apply modes class list to body
  const body = document.querySelector('body');
  body.classList.toString().split(' ').forEach(className => {
    if (modeRegex.test(className)) body.classList.remove(className);
  });
  if (body.className.indexOf('st-mode') === -1) {
    body.classList.add('st-mode');
  }
  const updatedThemeName = `st-mode--${modeDisplayedName}`;
  body.classList.add(updatedThemeName);
  const modeStyleBlock = document.getElementById('st-mode-styleblock');
  if (!modeStyleBlock) {
    document.head.insertAdjacentHTML('beforeend', `<style id="st-mode-styleblock">${modeStyles[modeName]}</style>`);
  } else {
    modeStyleBlock.innerText = modeStyles[modeName];
  }
};

/**
 * custom hook for uncontrolled version of Mode
 * for use in ThemeProvider
 * this allows users to set a starting mode with "defaultMode"
 * but any subsequent changes to that prop are ignored
 * in favor of whatever is in the internal state
 */
function useMode({
  defaultMode = 'auto',
  storageId,
  getModeFromStore,
  setModeToStore
} = {}) {
  const [modeSetting, setModeSetting] = useState();
  const [modeDisplayed, setmodeDisplayed] = useState();
  const refModeSetting = useRef(modeSetting); // to prevent stale state in closure for userEffect

  useEffect(() => {
    refModeSetting.current = modeSetting;
  });

  // for handling initial load and setting mode
  // if user has previously selected a mode and it's in localstore
  // then defer to that
  // otherwise use defaultMode
  // if defaultMode is auto, then detect from system
  // write all these to state and localstore
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let finalmode = '';
    let modeToDisplay = '';
    const modeFromStore = getModeFromStore && getModeFromStore() || getModeFromLocalStore(storageId);
    // console.log(`mode from store ${storageId} is ${modeFromStore}`);

    // if user has selected light or dark
    if (modeFromStore && modeFromStore !== 'auto') {
      finalmode = modeFromStore;
      modeToDisplay = modeFromStore;
    } else if (modeFromStore === 'auto' || !modeFromStore && defaultMode === 'auto') {
      // if user has selected auto, or user hasn't selected anything and defaultMode is auto
      finalmode = modeFromStore || defaultMode;
      modeToDisplay = getModeFromSystem();
    } else {
      // user hasn't selected anything, and defaultMode is not auto
      finalmode = defaultMode;
      modeToDisplay = defaultMode;
    }
    updateWindowStyles(modeToDisplay);
    if (setModeToStore) {
      setModeToStore(finalmode);
    } else {
      saveToStore(finalmode, storageId);
    }
    setModeSetting(finalmode);
    setmodeDisplayed(modeToDisplay);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const updateModeAndWindow = useCallback(newmode => {
    // console.log(`updateModeAndWindow called: updating mode to ${newmode}`);
    const modeToDisplay = newmode === 'auto' ? getModeFromSystem() : newmode;
    updateWindowStyles(modeToDisplay);
    if (setModeToStore) {
      setModeToStore(newmode);
    } else {
      saveToStore(newmode, storageId);
    }
    setModeSetting(newmode);
    setmodeDisplayed(modeToDisplay);
  }, [storageId, setModeToStore]);

  // listen for changes to user's device mode
  // so we can change dark/light mode when user updates their device preference
  useEffect(() => {
    function updateOnEventChange(event) {
      // console.log(`detected event change ${refModeSetting.current}`);
      if (refModeSetting.current === 'auto') {
        const newmode = event.matches ? 'dark' : 'light';
        updateWindowStyles(newmode);
        setmodeDisplayed(newmode);
        // console.log(`new mode is ${newmode}`);
      }
    }

    // older versions of iOS don't support matchMedia.addEventListener
    // once iOS 15 lands, go back to addEventListener('change', callback)
    // console.log('setting event listener for dark mode');
    window.matchMedia('(prefers-color-scheme: dark)').addListener(updateOnEventChange);
    return () => window.matchMedia('(prefers-color-scheme: dark)').removeListener(updateOnEventChange);
  }, []);
  function toggleMode() {
    // toggle modeSetting, can't be used when in auto
    if (modeSetting !== 'auto') {
      // console.log(`toggle mode from ${mode}`);
      updateModeAndWindow(modeSetting === 'light' ? 'dark' : 'light');
    }
  }
  return {
    modeSetting,
    modeDisplayed,
    updateMode: updateModeAndWindow,
    updateWindowStyles,
    toggleMode
  };
}
const useModeDocs = () => /*#__PURE__*/React.createElement("div", null, "placeholder for storybook");
useModeDocs.propTypes = {
  /** The current user setting for the color mode. */
  mode: PropTypes.oneOf(['auto', 'light', 'dark']).isRequired,
  /** The color mode that is being displayed based on the user setting. */
  modeDisplayed: PropTypes.oneOf(['light', 'dark']).isRequired,
  /** Updates the user setting to this specific modeSetting, and also updates the data store. */
  updateMode: PropTypes.func.isRequired,
  /** Toggles the user setting for the mode. Only works if current setting is not `auto`. */
  toggleMode: PropTypes.func.isRequired,
  /** Updates appearance of color mode without updating the data store. */
  updateWindowStyles: PropTypes.func.isRequired
};

export { useMode as default, modeStyles, updateWindowStyles, useModeDocs };
